import { enrolmentSchema, recoveryCodesSchema } from '@wodi/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { Button, LoadingBlock, Notice, TextField } from '@wodi/ui';
import QRCode from 'qrcode';
import { useEffect, useState } from 'react';

import { api, ApiError } from '../api/client.ts';
import { safeNext, sessionQuery } from '../api/session.ts';
import styles from './sign-in.module.css';
import { useSignOut } from './use-sign-out.ts';

const route = getRouteApi('/sign-in/set-up-2fa');

/** Groups the key in fours so it can be read out and typed without losing place. */
const grouped = (secret: string) => secret.replace(/(.{4})/g, '$1 ').trim();

function messageOf(error: unknown): string {
  return error instanceof ApiError ? error.message : 'Something went wrong. Try again.';
}

export function SignInSetupPage() {
  const { next } = route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const signOut = useSignOut();
  const [code, setCode] = useState('');
  const [qr, setQr] = useState<string | null>(null);

  // A query, not an effect, so React's double render in development can't start two
  // enrolments with different secrets.
  const enrolment = useQuery({
    queryKey: ['two-factor-enrolment'],
    queryFn: () => api('POST', '/auth/two-factor/enrolment', { schema: enrolmentSchema }),
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
  });

  useEffect(() => {
    if (enrolment.data === undefined) return;
    let cancelled = false;
    void QRCode.toDataURL(enrolment.data.otpauthUri, {
      margin: 1,
      width: 200,
      color: { dark: '#000000', light: '#ffffff' },
    }).then((url) => {
      if (!cancelled) setQr(url);
    });
    return () => {
      cancelled = true;
    };
  }, [enrolment.data]);

  const confirm = useMutation({
    mutationFn: (value: string) =>
      api('POST', '/auth/two-factor/enrolment/confirm', {
        body: { code: value },
        schema: recoveryCodesSchema,
      }),
    onError: () => {
      setCode('');
    },
  });

  const finish = async () => {
    queryClient.removeQueries({ queryKey: ['two-factor-enrolment'] });
    await queryClient.invalidateQueries({ queryKey: sessionQuery.queryKey });
    await navigate({ to: safeNext(next) });
  };

  if (confirm.data !== undefined) {
    return (
      <main className={styles.page}>
        <div className={styles.column}>
          <div className={styles.brand}>Wodi</div>
          <h1 className={styles.title}>Save your recovery codes</h1>
          <p>
            If you lose your phone, each of these codes lets you sign in once. Write them down or
            print this page, and keep them somewhere safe. They won't be shown again.
          </p>
          <ul className={styles.codes} aria-label="Recovery codes">
            {confirm.data.recoveryCodes.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
          <div className={styles.actions}>
            <Button variant="primary" onPress={() => void finish()}>
              I've saved these codes
            </Button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className={styles.page}>
      <div className={styles.column}>
        <div className={styles.brand}>Wodi</div>
        <h1 className={styles.title}>Set up two-factor authentication</h1>
        <p className={styles.lead}>
          Your role needs a code from your phone as well as your password.
        </p>
        {enrolment.isPending ? <LoadingBlock label="Preparing your setup code" rows={2} /> : null}
        {enrolment.isError ? (
          <Notice
            tone="error"
            action={
              <Button dense onPress={() => void enrolment.refetch()}>
                Try again
              </Button>
            }
          >
            {messageOf(enrolment.error)}
          </Notice>
        ) : null}
        {enrolment.data === undefined ? null : (
          <>
            <ol className={styles.steps}>
              <li>
                Install an authenticator app on your phone, such as Google Authenticator or
                Microsoft Authenticator.
              </li>
              <li>
                <p>In the app, add an account and scan this code.</p>
                {qr === null ? null : (
                  <img
                    className={styles.qr}
                    src={qr}
                    alt="QR code for setting up Wodi in an authenticator app"
                  />
                )}
                <p>
                  Can't scan it? Enter this key instead:{' '}
                  <span className={styles.secret}>{grouped(enrolment.data.secret)}</span>
                </p>
              </li>
              <li>Enter the six-digit code the app shows.</li>
            </ol>
            {confirm.isError ? <Notice tone="error">{messageOf(confirm.error)}</Notice> : null}
            <form
              className={styles.form}
              noValidate
              onSubmit={(event) => {
                event.preventDefault();
                if (code.trim().length >= 6) confirm.mutate(code.trim());
              }}
            >
              <TextField
                label="Code"
                autoComplete="one-time-code"
                inputMode="numeric"
                value={code}
                onChange={setCode}
              />
              <div className={styles.actions}>
                <Button
                  type="submit"
                  variant="primary"
                  isPending={confirm.isPending}
                  pendingLabel="Turning on…"
                >
                  Turn on two-factor
                </Button>
                <Button variant="quiet" onPress={signOut}>
                  Sign out
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </main>
  );
}
